import React from 'react';

import clsx from 'clsx';

import { BankAccount } from 'api/TruvApi';
import { CheckedImage } from 'assets/icons';
import { BankAccountContainerType } from 'enums/BankAccountContainerType';
import useMobileCheck from 'hooks/useMobileCheck';
import useTabletCheck from 'hooks/useTabletCheck';

import styles from './BankAccountContainer.module.scss';

export interface BankAccountContainerProps {
  containerType: BankAccountContainerType;
  bankAccount?: BankAccount;
  className?: string;
  onClick?: () => void;
}

enum MaxBankNameSymbols {
  Mobile = 7,
  Tablet = 12,
  Desktop = 17,
}

const BankAccountContainer = ({
  containerType,
  bankAccount,
  className,
  onClick,
  ...restProps
}: BankAccountContainerProps) => {
  const isMobile = useMobileCheck();
  const isTablet = useTabletCheck();

  const convertBankName = (bankName: string | undefined) => {
    if (!bankName) {
      return null;
    }

    let maxBankNameSymbols = MaxBankNameSymbols.Desktop;

    if (isMobile) {
      maxBankNameSymbols = MaxBankNameSymbols.Mobile;
    } else if (isTablet) {
      maxBankNameSymbols = MaxBankNameSymbols.Tablet;
    }

    const convertedBankName = bankName.substring(0, maxBankNameSymbols);

    if (convertedBankName.length !== bankName.length) {
      return convertedBankName + '...';
    }

    return bankName;
  };

  return (
    <>
      <button
        type="button"
        className={clsx(
          styles.bankAccountContainer,
          className,
          containerType === BankAccountContainerType.Select && styles.hoveredContainer,
        )}
        onClick={onClick}
        disabled={containerType === BankAccountContainerType.Loading}
        {...restProps}
      >
        {containerType === BankAccountContainerType.Loading && (
          <div className={styles.containerWithLoader}>
            <div className={styles.dotsElastic}></div>
          </div>
        )}
        {(containerType === BankAccountContainerType.Done || containerType === BankAccountContainerType.Select) && (
          <>
            <div className={styles.containerWithContent}>
              <div className={styles.section}>
                <div className={styles.sectionHeader}>Bank Name</div>
                <div className={styles.sectionContent}>{convertBankName(bankAccount?.bankName) || '—'}</div>
              </div>
              <div className={styles.section}>
                <div className={styles.sectionHeader}>Routing Number</div>
                <div className={styles.sectionContent}>{bankAccount?.routingNumber || '—'}</div>
              </div>
              <div className={styles.section}>
                <div className={styles.sectionHeader}>Account Type</div>
                <div className={styles.sectionContent}>{bankAccount?.accountType || '—'}</div>
              </div>
              <div className={styles.section}>
                <div className={styles.sectionHeader}>Account Number</div>
                <div className={styles.sectionContent}>{bankAccount?.accountNumber || '—'}</div>
              </div>
              {isMobile || (containerType === BankAccountContainerType.Done && <CheckedImage />)}
            </div>
          </>
        )}
      </button>
    </>
  );
};

export default BankAccountContainer;
